import { ReportData, FamilyUser, MessageThread } from './api';

// デバッグモードでのモックデータ
export const mockUser: FamilyUser = {
  line_user_id: 'mock-user-123',
  display_name: '田中 花子',
  picture_url: undefined,
  resident_ids: ['resident-001'],
  notification_settings: {
    enabled: true,
    time_start: '08:00',
    time_end: '20:00',
    frequency: 'daily',
  },
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

export const mockProfile = {
  userId: 'mock-user-123',
  displayName: '田中 花子',
  pictureUrl: undefined,
  statusMessage: undefined,
};

export const mockTodayReport: ReportData = {
  resident_id: 'resident-001',
  date: new Date().toISOString().split('T')[0],
  highlights: [
    '朝の散歩で笑顔が見られました',
    '昼食を完食されました',
    '午後のレクリエーションに積極的に参加'
  ],
  vitals: {
    temperature: 36.5,
    blood_pressure_sys: 125,
    blood_pressure_dia: 78,
    pulse: 72,
    spo2: 98,
  },
  activities: ['散歩', 'レクリエーション', '入浴'],
  meals: {
    breakfast: true,
    lunch: true,
    snack: true,
  },
  hydration_ml: 1200,
  bath: true,
  toilet_count: 4,
  medications: true,
  comment: '今日は一日を通して元気に過ごされました。特に散歩の時間はとても楽しそうでした。',
  status: 'published',
  alert_level: 'normal',
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};

export const mockHistoryReports: ReportData[] = [
  mockTodayReport,
  {
    ...mockTodayReport,
    date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    highlights: [
      '体温が少し高めでした',
      '水分補給をこまめに実施',
      '午後は休息を多めに取りました'
    ],
    vitals: {
      ...mockTodayReport.vitals,
      temperature: 37.2,
    },
    alert_level: 'attention',
    comment: '体温が少し高めでしたが、水分補給と休息で改善しました。',
  },
  {
    ...mockTodayReport,
    date: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    highlights: [
      'カラオケで大きな声で歌われました',
      '手芸活動に集中して取り組み',
      '友人との会話を楽しまれました'
    ],
    comment: 'カラオケでとても楽しそうに過ごされました。手先の動きも良好です。',
  },
];

// ==========================
// 複数患者データ
// ==========================
export const mockLinkedPatients = [
  {
    patient_id: 1,
    patient_name: '山田 太郎',
    patient_display_name: 'おじいちゃん',
    role: 'admin',
    office_id: 'office-001',
    office_name: 'ひまわり通所介護事業所',
    relationship: '夫',
    // 家族連携のため、実API同様 birth_date/gender は返さない（null）
    birth_date: null,
    gender: null,
  },
  {
    patient_id: 2,
    patient_name: '佐藤 花子',
    patient_display_name: undefined,
    role: 'viewer',
    office_id: 'office-001',
    office_name: 'ひまわり通所介護事業所',
    relationship: '本人',
    // 本人連携のため、実API同様 patients テーブルの値を返す
    birth_date: '1948-03-15',
    gender: 'female',
  },
];

// ==========================
// 家族一覧データ
// ==========================
export const mockFamilies = [
  {
    line_user_id: 'mock-user-123',
    display_name: '田中 花子',
    patient_id: 1,
    patient_name: '山田 太郎',
    patient_display_name: 'おじいちゃん',
    relationship: '孫',
    role: 'admin',
    created_at: '2025-01-15T10:30:00Z',
    updated_at: '2025-01-15T10:30:00Z',
  },
  {
    line_user_id: 'mock-user-456',
    display_name: '山田 次郎',
    patient_id: 1,
    patient_name: '山田 太郎',
    patient_display_name: undefined,
    relationship: '息子',
    role: 'admin',
    created_at: '2025-01-10T09:00:00Z',
    updated_at: '2025-01-10T09:00:00Z',
  },
  {
    line_user_id: 'mock-user-789',
    display_name: '山田 美咲',
    patient_id: 1,
    patient_name: '山田 太郎',
    patient_display_name: undefined,
    relationship: '娘',
    role: 'viewer',
    created_at: '2025-01-12T14:20:00Z',
    updated_at: '2025-01-12T14:20:00Z',
  },
];

// ==========================
// 連絡帳データ（日付別）- 3日ごと
// ==========================
/** 連絡帳の日付文字列。メッセージの引用元（reply_to.source_date）も同じ算出に揃える */
const getContactDateStr = (daysAgo: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().split('T')[0];
};

/** 利用者1（山田太郎）の、メッセージから引用される日の連絡帳コメント */
const PATIENT1_CONTACT_COMMENTS: { [daysAgo: number]: string } = {
  0: '【特変】昼食後、トイレへ移動される際に右膝の痛みを訴えられ、立ち上がりに時間がかかっていました。看護師が確認したところ熱感や腫れは見られませんでしたが、念のため午後の機能訓練はお休みし、座ってできる体操のみ行っています。ご自宅でも痛みが続くようでしたら、かかりつけ医へのご相談をお願いいたします。',
  3: '午前中の機能訓練では平行棒内の歩行を5往復行い、ふらつきも少なく安定していました。入浴後は「さっぱりした」と笑顔を見せてくださいました。昼食は主食を少し残されましたが、おかずは完食です。午後の書道レクでは「春」の字を丁寧に書かれ、他の利用者様からも褒められていました。',
  6: '入浴時、背中の皮膚に乾燥が見られたため、入浴後に保湿クリームを塗布しました。かゆみの訴えはありません。午後はカラオケで「北国の春」を歌われ、手拍子をしながらとても楽しそうに過ごされていました。',
};

/** 上記以外の日の連絡帳コメント。日付ごとに順番に使う */
const ROTATING_CONTACT_COMMENTS = [
  'リハビリ体操では、腕を大きく回す動きにもしっかりついてこられていました。昼食は完食され、おやつの時間には他の利用者様と昔のお仕事のお話で盛り上がっていました。',
  '本日はやや眠そうなご様子で、午前中はうとうとされる時間がありました。バイタルは安定しています。午後からは調子が戻り、ボール体操にも参加されました。水分はこまめにお声がけし、しっかり摂っていただいています。',
  '個別機能訓練で、手すりにつかまっての階段昇降（4段）を2往復行いました。「前より楽になった」とおっしゃっていました。帰りの送迎車の中でも、お孫さんのお話を嬉しそうにされていました。',
  '午後の園芸レクで、プランターにミニトマトの苗を植えました。手先を使う作業も丁寧で、「実がなるのが楽しみ」と話されていました。入浴・昼食とも問題なく過ごされています。',
];

const getContactComment = (daysAgo: number, patientId: number): string =>
  (patientId === 1 && PATIENT1_CONTACT_COMMENTS[daysAgo]) ||
  ROTATING_CONTACT_COMMENTS[Math.floor(daysAgo / 3) % ROTATING_CONTACT_COMMENTS.length];

const generateContactLogForDate = (daysAgo: number, patientId: number = 1) => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  const dateStr = getContactDateStr(daysAgo);
  const comment = getContactComment(daysAgo, patientId);

  // 21日ごとに欠席（データあり）
  const isAbsent = daysAgo % 21 === 0 && daysAgo !== 0;

  // 次回来所日のAPI3状態をローカル開発で確認できるようにする。
  // モックは3日おきの日付にしか連絡帳を持たない（それ以外の日は404）ため、
  // データが存在する日を1つずつ遡ると
  // 0:日付あり → 1:空文字 → 2:null と循環する。
  // 1 と 2 はどちらもタイトルごとトルツメされるため、画面上の見た目は同じになる。
  const nextVisitVariant = Math.floor(daysAgo / 3) % 3;
  const nextVisitDate = new Date(date);
  nextVisitDate.setDate(nextVisitDate.getDate() + 2);

  // content_idを事前計算（トップレベルとcontact_messageで共有）
  const contentId = isAbsent ? undefined : String(Math.floor(Date.now() / 1000) * 1000 + daysAgo + patientId * 10000);

  return {
    patient_id: patientId,
    date: dateStr,
    content_id: contentId,
    attendance_id: String(1000 + daysAgo + patientId * 10000),
    attendance_status: isAbsent ? 'absent' : 'present' as 'present' | 'absent',
    check_in_time: isAbsent ? undefined : '09:30:00',
    check_out_time: isAbsent ? undefined : '16:45:00',
    vitals: isAbsent ? undefined : {
      temp_c: Math.round((36.3 + Math.random() * 0.5) * 10) / 10,
      sbp: 120 + Math.floor(Math.random() * 20),
      dbp: 70 + Math.floor(Math.random() * 10),
      pulse: 65 + Math.floor(Math.random() * 15),
      spo2: 97 + Math.floor(Math.random() * 3),
    },
    meal: isAbsent ? undefined : {
      staple_food: 70 + Math.floor(Math.random() * 40),
      side_dish: 80 + Math.floor(Math.random() * 30),
      soup_food: 90 + Math.floor(Math.random() * 20),
      is_not_provided: false,
    },
    hydration: isAbsent ? undefined : {
      amount_ml: 800 + Math.floor(Math.random() * 600),
    },
    training: isAbsent ? undefined : {
      status: 1,
    },
    bathing: isAbsent ? undefined : {
      status: 1,
      is_recorded: true,
    },
    // APIは「非表示設定ならnull」「値なしなら空文字」を返す
    next_visit: nextVisitVariant === 2
      ? null
      : { date: nextVisitVariant === 0 ? nextVisitDate.toISOString().split('T')[0] : '' },
    contact_message: isAbsent ? undefined : {
      message_id: String(5000 + daysAgo + patientId * 10000),
      content_id: contentId,
      patient_id: patientId,
      subject: '今日のご様子',
      body: comment,
      created_at: `${dateStr}T16:00:00Z`,
      updated_at: `${dateStr}T16:00:00Z`,
    },
    photos: daysAgo % 9 === 0 && !isAbsent ? [
      {
        s3_key: `mock-photos/patient-${patientId}/${dateStr}-1.jpg`,
        description: '散歩中の一枚',
        created_at: `${dateStr}T11:00:00Z`,
      },
      {
        s3_key: `mock-photos/patient-${patientId}/${dateStr}-2.jpg`,
        description: 'レクリエーションの様子',
        created_at: `${dateStr}T11:00:00Z`,
      },
      {
        s3_key: `mock-photos/patient-${patientId}/${dateStr}-3.jpg`,
        description: '昼食の様子',
        created_at: `${dateStr}T11:00:00Z`,
      },
      {
        s3_key: `mock-photos/patient-${patientId}/${dateStr}-4.jpg`,
        description: '施設での一枚',
        created_at: `${dateStr}T11:00:00Z`,
      },
    ] : [],
    has_vitals: !isAbsent,
    has_meal: !isAbsent,
    has_hydration: !isAbsent,
    message_preview: isAbsent ? undefined : `${comment.slice(0, 20)}...`,
    created_at: `${dateStr}T16:00:00Z`,
  };
};

// 3日ごとにデータを生成（0, 3, 6, 9, 12, ... 90日分 = 31データポイント）
// 利用者1: 山田太郎 - 当日から3日ごと（0, 3, 6, ...日前）
const mockContactLogsPatient1 = Array.from({ length: 31 }, (_, i) => generateContactLogForDate(i * 3, 1));

// 利用者2: 佐藤花子 - 1日ずらし（1, 4, 7, ...日前）
const mockContactLogsPatient2 = Array.from({ length: 31 }, (_, i) => generateContactLogForDate(i * 3 + 1, 2));

export const mockContactLogs = [...mockContactLogsPatient1, ...mockContactLogsPatient2];

// ==========================
// 出席カレンダーデータ（3ヶ月分、3日ごと）
// ==========================
export const mockAttendanceCalendar = mockContactLogs.map(log => ({
  patient_id: log.patient_id,
  date: log.date,
  attendance_id: log.attendance_id,
  attendance_status: log.attendance_status,
  check_in_time: log.check_in_time,
  check_out_time: log.check_out_time,
  has_vitals: log.has_vitals,
  has_meal: log.has_meal,
  has_hydration: log.has_hydration,
  message_preview: log.message_preview,
  created_at: log.created_at,
}));

// ==========================
// 健康グラフデータ（30日分）
// ==========================
const generateHealthDataForDays = (days: number, patientId: number = 1, dayOffset: number = 0) => {
  const data = [];
  // 3日ごとにデータを生成（0, 3, 6, 9, 12, ... 90日分）
  for (let i = 0; i < days; i += 3) {
    const date = new Date();
    date.setDate(date.getDate() - i - dayOffset);
    const dateStr = date.toISOString().split('T')[0];

    data.push({
      patient_id: patientId,
      date: dateStr,
      attendance_id: String(2000 + i + patientId * 10000),
      vitals: {
        temp_c: 36.3 + (Math.random() * 0.6 - 0.2),
        sbp: 125 + Math.floor(Math.random() * 20 - 10),
        dbp: 75 + Math.floor(Math.random() * 10 - 5),
        pulse: 70 + Math.floor(Math.random() * 15 - 7),
        spo2: 98 + Math.floor(Math.random() * 2),
      },
      meals: {
        staple_intake: 7 + Math.floor(Math.random() * 4),
        side_intake: 8 + Math.floor(Math.random() * 3),
        soup_intake: 9 + Math.floor(Math.random() * 2),
        is_not_provided: false,
      },
      hydration_ml: 800 + Math.floor(Math.random() * 600),
    });
  }
  // 日付順にして返す
  const sortedData = data.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  return sortedData;
};

// 利用者1: 山田太郎 - 当日から
const mockHealthDataPatient1 = generateHealthDataForDays(90, 1, 0);
// 利用者2: 佐藤花子 - 1日ずらし
const mockHealthDataPatient2 = generateHealthDataForDays(90, 2, 1);

export const mockHealthDataExtended = [...mockHealthDataPatient1, ...mockHealthDataPatient2];

// ==========================
// 写真閲覧URL
// ==========================
/**
 * 動的にモック写真のview URLを生成する関数
 * S3キーから写真番号を抽出し、対応するモック画像URLを返す
 *
 * @param s3Key - S3キー（例: "mock-photos/patient-1/2025-11-28-1.jpg"）
 * @returns モック画像のURL
 */
export const getMockPhotoUrl = (s3Key: string): string => {
  // S3キーから写真番号を抽出（-1.jpg or -2.jpg）
  const photoNumberMatch = s3Key.match(/-(\d+)\.jpg$/);
  const photoNumber = photoNumberMatch ? photoNumberMatch[1] : '1';

  // 写真番号に応じて異なる画像URLを返す（3・4番目は public 配信のローカル画像）
  const mockUrls: { [key: string]: string } = {
    '1': 'https://t4.ftcdn.net/jpg/06/24/60/33/240_F_624603378_cC5ISSit31tzusYn5Is2cCNhNRgeUMq7.jpg',
    '2': 'https://as1.ftcdn.net/v2/jpg/05/28/17/66/1000_F_528176675_zTpPg0BEyd76YpDoIiFYJyG90BHdrSOp.jpg',
    '3': '/images/sample-food.jpg',
    '4': '/images/sample-person.png',
  };

  return mockUrls[photoNumber] || mockUrls['1'];
};

// ==========================
// 招待トークン検証結果
// ==========================
export const mockLinkTokenVerification = {
  patient_id: 1,
  patient_name: '山田 太郎',
  office_id: 'office-001',
  office_name: 'ひまわり通所介護事業所',
  company_name: '社会福祉法人ひまわり会',
  has_current_consent: false,
  // 新モデル third_party_consents 由来。mock_scenario=no_third_party_consent で false に切替可能。
  has_third_party_consent: true,
};

// 旧 mockCurrentConsent（第三者提供同意 / 患者単位）は PR-3 で /consent 画面が
// 通信の秘密同意へ切り替わったことにより参照元がゼロになったため、PR-4 で削除。
// 通信の秘密同意側の mock は apiClient.getCommunicationSecretConsentStatus() /
// getCommunicationSecretConsentDocument() の関数内 dev mock を参照。

// ==========================
// メッセージスレッド
// ==========================
const generateMessageDate = (daysAgo: number, hours: number, minutes: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  date.setHours(hours, minutes, 0, 0);
  return date.toISOString();
};

// 利用者1: 山田太郎のメッセージスレッド
const mockMessagesThreadPatient1 = {
  patient_id: 1,
  office_id: 'office-001',
  office_name: 'ひまわり通所介護事業所',
  office_phone: '03-1234-5678',
  threads: [
    {
      thread_id: 'thread-001',
      hide_from_patient: false,
      messages: [
        {
          message_id: 1705123456001,
          direction: 'outgoing' as const,
          content: 'ご連絡ありがとうございます。帰宅後も右膝を気にしていましたが、ゆっくりなら歩けているようです。明日の午前中に、かかりつけの整形外科を受診してきます。結果がわかり次第、またご連絡します。',
          sender_name: '田中太郎',
          created_at: generateMessageDate(0, 18, 30),
          confirmed_at: generateMessageDate(0, 18, 40),
          reply_to: {
            type: 'contact_comment' as const,
            source_date: getContactDateStr(0),
            source_content: PATIENT1_CONTACT_COMMENTS[0],
            facility_name: 'ひまわり通所介護事業所',
          },
        },
        {
          // 事業所からの返信（FAMILYAPP-343）。送信者を保存しないので sender_name は持たず、
          // 確認状態（confirmed_at）も持たない
          message_id: 1705123456011,
          direction: 'incoming' as const,
          content: '受診のご予定、承知いたしました。次回ご利用の際は受診結果を踏まえて訓練内容を調整いたしますので、医師から注意点などの指示があれば教えてください。どうぞお大事になさってください。',
          created_at: generateMessageDate(0, 18, 50),
        },
      ],
    },
    {
      thread_id: 'thread-002',
      hide_from_patient: true,
      messages: [
        {
          message_id: 1705123456002,
          direction: 'outgoing' as const,
          content: '書道の作品のこと、本人からも嬉しそうに聞きました。ただ、最近は家ではテレビを見て過ごすことが多く、夜中に何度も起きてしまうのが気になっています。デイサービスではお昼寝をしている時間は長いでしょうか？日中の過ごし方を教えていただけると助かります。',
          sender_name: '田中花子',
          created_at: generateMessageDate(3, 19, 0),
          confirmed_at: generateMessageDate(2, 9, 10),
          reply_to: {
            type: 'contact_comment' as const,
            source_date: getContactDateStr(3),
            source_content: PATIENT1_CONTACT_COMMENTS[3],
            facility_name: 'ひまわり通所介護事業所',
          },
        },
        {
          message_id: 1705123456013,
          direction: 'incoming' as const,
          content: 'ご相談ありがとうございます。デイサービスでは昼食後に20分ほど休憩されることがありますが、長くお休みになることはほとんどありません。夜しっかり眠れるよう、午後も体操やレクリエーションに参加していただけるよう声かけしてまいります。ご自宅でのご様子も、また教えていただけると嬉しいです。',
          created_at: generateMessageDate(2, 9, 30),
        },
        {
          message_id: 1705123456014,
          direction: 'outgoing' as const,
          content: 'ありがとうございます。日中しっかり活動できているようで安心しました。家でも夕方に少し散歩するようにしてみます。',
          sender_name: '田中花子',
          created_at: generateMessageDate(2, 12, 0),
          confirmed_at: generateMessageDate(2, 13, 0),
        },
      ],
    },
    {
      // message_id は thread_id との組でしか一意にならないため、別スレッドと同じ値を意図的に使う
      thread_id: 'thread-003',
      hide_from_patient: false,
      messages: [
        {
          message_id: 1705123456002,
          direction: 'outgoing' as const,
          content: '保湿していただきありがとうございます。冬場は毎年乾燥がひどく、家でも夜にかゆがって掻いてしまうことがあります。皮膚科で処方された塗り薬があるので、次回の荷物に入れておきます。入浴後に塗っていただくことは可能でしょうか。',
          sender_name: '田中太郎',
          created_at: generateMessageDate(6, 20, 0),
          reply_to: {
            type: 'contact_comment' as const,
            source_date: getContactDateStr(6),
            source_content: PATIENT1_CONTACT_COMMENTS[6],
            facility_name: 'ひまわり通所介護事業所',
          },
        },
      ],
    },
    {
      thread_id: 'thread-004',
      hide_from_patient: false,
      messages: [
        {
          message_id: 1705123456003,
          direction: 'outgoing' as const,
          content: 'ミニトマトの苗を植えたと、嬉しそうに話してくれました。昔は家庭菜園をよくやっていたので、懐かしかったのだと思います。実がなったら写真を見せていただけると本人も喜ぶと思います。',
          sender_name: '田中花子',
          created_at: generateMessageDate(9, 18, 0),
          confirmed_at: generateMessageDate(8, 9, 0),
          reply_to: {
            type: 'contact_comment' as const,
            source_date: getContactDateStr(9),
            source_content: getContactComment(9, 1),
            facility_name: 'ひまわり通所介護事業所',
          },
        },
        {
          message_id: 1705123456015,
          direction: 'incoming' as const,
          content: '素敵なお話をありがとうございます。実がなりましたら、ぜひ写真を撮って連絡帳でお送りしますね。水やりも山田様に担当していただこうと思っています。',
          created_at: generateMessageDate(8, 9, 15),
        },
      ],
    },
    {
      thread_id: 'thread-005',
      hide_from_patient: false,
      messages: [
        {
          message_id: 1705123456004,
          direction: 'outgoing' as const,
          content: 'いつもお世話になっております。来週の水曜日は、午前中に眼科の定期受診があるためお休みさせていただきます。木曜日はいつも通り利用予定です。よろしくお願いいたします。',
          sender_name: '田中太郎',
          created_at: generateMessageDate(2, 14, 10),
          confirmed_at: generateMessageDate(2, 14, 20),
        },
        {
          message_id: 1705123456016,
          direction: 'incoming' as const,
          content: 'ご連絡ありがとうございます。来週水曜日のお休み、承知いたしました。送迎の予定も変更しておきます。木曜日お待ちしております。',
          created_at: generateMessageDate(2, 14, 30),
        },
      ],
    },
    {
      thread_id: 'thread-006',
      hide_from_patient: false,
      messages: [
        {
          message_id: 1705123456006,
          direction: 'outgoing' as const,
          content: 'おはようございます。昨晩から少し咳が出ており、今朝の体温は36.9℃でした。本人は行く気満々なのですが、本日の入浴は控えていただけますでしょうか。日中に熱が上がるなど体調が悪そうであれば、早めにご連絡いただけると助かります。',
          sender_name: '田中花子',
          created_at: generateMessageDate(1, 7, 45),
        },
      ]
    },
    {
      thread_id: 'thread-007',
      hide_from_patient: false,
      messages: [
        {
          message_id: 1705123456009,
          direction: 'outgoing' as const,
          content: 'おはようございます。家の前の道路が今週いっぱい工事中のため、送迎の際は一本手前の角でお待ちしていてもよろしいでしょうか。お手数をおかけしますが、よろしくお願いいたします。',
          sender_name: '田中太郎',
          created_at: generateMessageDate(0, 8, 0),
          confirmed_at: generateMessageDate(0, 8, 20),
        },
        {
          message_id: 1705123456012,
          direction: 'incoming' as const,
          content: 'ご連絡ありがとうございます。送迎担当にも共有し、今週は一本手前の角でお迎えいたします。お待たせしないよう、到着前にお電話いたしますね。',
          created_at: generateMessageDate(0, 8, 30),
        },
      ]
    },
  ],
  has_more: false,
};

// 利用者2: 佐藤花子のメッセージスレッド
const mockMessagesThreadPatient2: MessageThread = {
  patient_id: 2,
  office_id: 'office-001',
  office_name: 'ひまわり通所介護事業所',
  office_phone: '03-1234-5678',
  threads: [],
  has_more: false,
};

// 患者IDで取得できるオブジェクト
export const mockMessagesThreads: { [patientId: number]: MessageThread } = {
  1: mockMessagesThreadPatient1,
  2: mockMessagesThreadPatient2,
};

// 後方互換性のため、デフォルトは利用者1
export const mockMessagesThread = mockMessagesThreadPatient1;

// 本番配信ホストの判定（CloudFront デフォルトドメイン / LIFF カスタムドメイン）。
// endsWith は先頭ドット付きで判定し、xcontact-note.rehab.cloud や
// cloudfront.net.evil.example のような別ホストへの誤マッチを避ける
export const isProductionHostname = (hostname: string): boolean =>
  hostname.endsWith('.cloudfront.net') ||
  hostname === 'contact-note.rehab.cloud' ||
  hostname.endsWith('.contact-note.rehab.cloud');

export const isDevelopmentMode = () => {
  // 環境変数で強制的にモックモードを有効化
  if ((import.meta as any).env?.VITE_FORCE_MOCK_MODE === 'true') {
    console.log('[Mock Mode] Forced by VITE_FORCE_MOCK_MODE');
    return true;
  }

  // CloudFront / カスタムドメイン経由のアクセスはすべて本番環境として扱う
  if (isProductionHostname(window.location.hostname)) {
    return false;
  }

  // LINE内ブラウザの場合は本番環境として扱う
  if ((window as any).liff) {
    return false;
  }

  // ローカル開発環境のみモックを使用
  return window.location.hostname === 'localhost' ||
         window.location.hostname === '127.0.0.1';
};

// ==========================
// モックシナリオ切り替え
// ==========================
// URLパラメータ ?mock_scenario=xxx でモック動作を切り替え可能
//   no_third_party_consent   : 第三者提供同意未取得の招待リンク（NotYetAvailable 画面）
//   no_communication_consent : 通信の秘密同意未取得（/consent へ強制遷移）
//   self                     : 利用者本人として登録（プロフィールで生年月日・性別スキップ）
//   family                   : 家族として登録（デフォルト）
export const getMockScenario = (): string => {
  const params = new URLSearchParams(window.location.search);
  return params.get('mock_scenario') || 'family';
};